// @sb-ai-platform/schemas — per-session memory-scope normalizer (fn-3)
//
// The single canonical normalization layer for the `POST /sessions` top-level
// `memory` body field (an array of memory scope names, e.g.
// `["user","firm","matter"]`). The wire schema keeps the field LOOSE
// (`string[]`) deliberately: a strict TypeBox literal union would make Ajv
// reject an unknown scope with a generic 400 BEFORE the handler runs, and the
// fn-3 contract requires the exact `memory_config_invalid` error code. The
// scope enum + duplicate rejection therefore live HERE (platform rule:
// `schemas` owns request normalizers — precedent `session-tool-config.ts`),
// and the control-plane route maps {@link SessionMemoryValidationError} to
// `400 memory_config_invalid`.
//
// **Boundary discipline**: lives in `schemas`; imports nothing from any impl
// package. The boundary lint stays green.
//
// Context-dependent validation (a `matter` scope without a bound `matter_id`;
// a `user` scope from a service principal; the flag/capability gate; the
// agent-toolset precondition) is deliberately NOT here — the normalizer is
// pure shape. Those checks live in the route, which has the context.
/**
 * The closed set of session memory scopes, in CANONICAL ORDER
 * (`user`, `firm`, `matter`). The normalizer returns scopes in this
 * order regardless of request order, and the public `memory` field on
 * the session detail projection renders in this order (fn-3 R4).
 * String-aligned with `SessionMemoryScope` in `@sb-ai-platform/provider`
 * and the persisted union in `packages/migrations` (mirrored, not
 * imported — `schemas` depends on neither).
 */
export const SESSION_MEMORY_SCOPES = ['user', 'firm', 'matter'];
/**
 * Thrown by {@link normalizeSessionMemoryScopes} on the first failing check.
 * Framework-neutral — the control-plane route maps it to a Fastify
 * `400 { error: "memory_config_invalid" }`; a future BFF/tRPC consumer maps
 * it to `BAD_REQUEST`. The message is safe to surface (it echoes at most a
 * bounded slice of the offending value).
 */
export class SessionMemoryValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'SessionMemoryValidationError';
    }
}
/** Runtime guard for one {@link SessionMemoryScopeValue}. Exact lowercase literals only. */
export function isSessionMemoryScope(value) {
    return value === 'user' || value === 'firm' || value === 'matter';
}
/**
 * Bounded, printable echo of a bad scope value for the error message —
 * never reflect an unbounded/hostile caller string back on the wire.
 */
function describeBadScope(value) {
    if (typeof value !== 'string')
        return `a ${typeof value}`;
    const slice = value.length > 32 ? `${value.slice(0, 32)}…` : value;
    return JSON.stringify(slice);
}
/**
 * Validate + normalize the caller `memory` field. Returns the requested
 * scopes in CANONICAL ORDER (`user`, `firm`, `matter`) on success, or
 * `undefined` when no memory was requested; throws
 * {@link SessionMemoryValidationError} on the first violation.
 *
 * Rules (fn-3 R3/R6):
 *   - `null` / `undefined` → `undefined` (no memory requested).
 *   - `[]` → `undefined` — an EMPTY array normalizes to ABSENT, so it
 *     short-circuits BEFORE the route's flag/capability gate (a no-op,
 *     never a 400).
 *   - every entry must be one of the exact lowercase literals
 *     `user` | `firm` | `matter` — no trim/case-fold normalization.
 *   - duplicates are rejected.
 *   - anything that is not an array (including a bare string) is rejected.
 */
export function normalizeSessionMemoryScopes(input) {
    if (input === null || input === undefined)
        return undefined;
    if (!Array.isArray(input)) {
        throw new SessionMemoryValidationError('memory must be an array of scope names');
    }
    if (input.length === 0)
        return undefined;
    // The closed enum has 3 members and duplicates are rejected, so any
    // longer array is invalid by construction — reject before iterating a
    // hostile oversized payload.
    if (input.length > SESSION_MEMORY_SCOPES.length) {
        throw new SessionMemoryValidationError(`memory accepts at most ${SESSION_MEMORY_SCOPES.length} scopes (${SESSION_MEMORY_SCOPES.join(', ')})`);
    }
    const seen = new Set();
    for (const entry of input) {
        if (!isSessionMemoryScope(entry)) {
            throw new SessionMemoryValidationError(`unknown memory scope ${describeBadScope(entry)}; expected one of ${SESSION_MEMORY_SCOPES.join(', ')}`);
        }
        if (seen.has(entry)) {
            throw new SessionMemoryValidationError(`duplicate memory scope "${entry}"`);
        }
        seen.add(entry);
    }
    // Canonical order regardless of request order (fn-3 R4).
    return SESSION_MEMORY_SCOPES.filter((scope) => seen.has(scope));
}
//# sourceMappingURL=session-memory.js.map